import { useState } from "react";
import type { AnalyticsFilterParams } from "../../services/api.service";
import { AnalyticsEngagement } from "./AnalyticsEngagement";
import { AnalyticsExport } from "./AnalyticsExport";
import { EMPTY_FILTER_DISPLAY, type FilterDisplayState } from "./AnalyticsFilters";
import { AnalyticsSessions } from "./AnalyticsSessions";
import { AnalyticsStudentDetail } from "./AnalyticsStudentDetail";
import { AnalyticsStudents } from "./AnalyticsKids";
import { cn } from "../../lib/utils";

type SubTab = "engagement" | "sessions" | "students" | "export";

const TABS: { id: SubTab; label: string }[] = [
    { id: "engagement", label: "Engagement" },
    { id: "sessions", label: "Sessions" },
    { id: "students", label: "Students" },
    { id: "export", label: "Export" },
];

export const Analytics = () => {
    const [subTab, setSubTab] = useState<SubTab>("engagement");
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
    const [filters, setFilters] = useState<AnalyticsFilterParams>({});
    const [filterDisplay, setFilterDisplay] = useState<FilterDisplayState>(EMPTY_FILTER_DISPLAY);

    const handleStudentClick = (userId: number) => {
        setSelectedUserId(userId);
        setSelectedSessionId(null);
    };

    const handleSessionClick = (sessionId: string) => {
        setSelectedSessionId(sessionId);
    };

    const handleBack = () => {
        if (selectedSessionId) {
            setSelectedSessionId(null);
        } else if (selectedUserId) {
            setSelectedUserId(null);
        }
    };

    // If a student is selected, show drill-down view
    if (selectedUserId !== null) {
        return (
            <div className="p-6">
                <AnalyticsStudentDetail
                    userId={selectedUserId}
                    sessionId={selectedSessionId}
                    onBack={handleBack}
                    onSessionClick={handleSessionClick}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border bg-card shadow-sm">
            {/* Tabs */}
            <div className="flex gap-1 border-b border-border px-4 pt-4 md:px-6">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        className={cn(
                            "px-5 py-3 text-sm font-medium border-b-2 transition-colors",
                            subTab === tab.id
                                ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                        )}
                        onClick={() => setSubTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="min-h-0 min-w-0 flex-1 overflow-auto p-4 md:p-6">
                {subTab === "engagement" && (
                    <AnalyticsEngagement filters={filters} onFiltersChange={setFilters} filterDisplay={filterDisplay} onFilterDisplayChange={setFilterDisplay} />
                )}
                {subTab === "sessions" && (
                    <AnalyticsSessions filters={filters} onFiltersChange={setFilters} filterDisplay={filterDisplay} onFilterDisplayChange={setFilterDisplay} />
                )}
                {subTab === "students" && (
                    <AnalyticsStudents
                        filters={filters}
                        onFiltersChange={setFilters}
                        onStudentClick={handleStudentClick}
                        filterDisplay={filterDisplay}
                        onFilterDisplayChange={setFilterDisplay}
                    />
                )}
                {subTab === "export" && (
                    <AnalyticsExport filters={filters} onFiltersChange={setFilters} filterDisplay={filterDisplay} onFilterDisplayChange={setFilterDisplay} />
                )}
            </div>
        </div>
    );
};
